/**
 * RelatedDocuments component - displays and manages documents related to an entity
 * Supports drag-and-drop upload with automatic relationship creation
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Upload,
  Modal,
  Tag,
  Tooltip,
  Popconfirm,
  Empty,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { UploadProps } from "antd/es/upload/interface";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const { Dragger } = Upload;

interface RelatedDocumentsProps {
  entityId: string;
  entityModule: string; // coreBOS module name: "Accounts", "Contacts", "Potentials"
  title?: string;
  compact?: boolean; // Compact mode for sidebar - shows only document name and eye button
}

interface DocumentRecord {
  id: string;
  notes_title: string;
  filename: string;
  filetype: string;
  filesize: string;
  createdtime: string;
}

// Helper to get file icon based on type
const getFileIcon = (filetype: string) => {
  if (filetype?.includes("pdf")) return <FilePdfOutlined />;
  if (filetype?.includes("image")) return <FileImageOutlined />;
  if (filetype?.includes("word") || filetype?.includes("document"))
    return <FileWordOutlined />;
  if (filetype?.includes("excel") || filetype?.includes("spreadsheet"))
    return <FileExcelOutlined />;
  return <FileOutlined />;
};

// Helper to format file size
const formatFileSize = (bytes: number) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const RelatedDocuments: React.FC<RelatedDocumentsProps> = ({
  entityId,
  entityModule,
  title = "Related Documents",
  compact = false,
}) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);

  // Fetch related documents using VQL with related entity syntax
  const fetchDocuments = async () => {
    if (!entityId) return;

    setLoading(true);
    try {
      // Use VQL related entity query syntax to find documents related to this entity
      const response = await axios.post("/api/direct/query", {
        query: `SELECT id, notes_title, filename, filetype, filesize, createdtime
                FROM Documents
                WHERE related.${entityModule} = '${entityId}'
                LIMIT 100;`,
      });

      if (response.data.success) {
        setDocuments(response.data.result || []);
      } else {
        console.error("Failed to fetch documents:", response.data.error);
        // Fallback: try getRelatedRecords API
        const fallbackResponse = await axios.post("/api/direct/get-related", {
          id: entityId,
          module: entityModule,
          related_module: "Documents",
          query_params: {
            limit: 100,
          },
        });
        if (fallbackResponse.data.success) {
          setDocuments(fallbackResponse.data.result || []);
        }
      }
    } catch (error) {
      console.error("Error fetching related documents:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [entityId, entityModule]);

  // Handle file upload
  const handleUpload = async (file: File) => {
    setUploading(true);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async () => {
        const base64 = (reader.result as string).split(",")[1];

        // 1. Create document
        const uploadResponse = await axios.post("/api/direct/upload-document", {
          filename: file.name,
          filecontent: base64,
        });

        if (!uploadResponse.data.success) {
          message.error(uploadResponse.data.error || "Upload failed");
          setUploading(false);
          return;
        }

        const newDocId = uploadResponse.data.result.id;

        // 2. Create relationship between entity and document
        const relationResponse = await axios.post("/api/direct/set-relation", {
          relate_this_id: entityId,
          with_these_ids: [newDocId],
        });

        if (relationResponse.data.success) {
          message.success("Document uploaded and linked successfully");
          setUploadModalVisible(false);
          fetchDocuments(); // Refresh list
        } else {
          message.warning(
            "Document uploaded but failed to link: " +
              (relationResponse.data.error || "Unknown error")
          );
        }

        setUploading(false);
      };

      reader.onerror = () => {
        message.error("Failed to read file");
        setUploading(false);
      };
    } catch (error) {
      message.error("Upload failed");
      console.error("Upload error:", error);
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  // Handle download
  const handleDownload = async (record: DocumentRecord) => {
    try {
      const response = await axios.post("/api/direct/retrieve-attachment", {
        doc_ids: [record.id],
        return_file: true,
      });

      if (response.data.success && response.data.result) {
        const result = response.data.result;
        const fileData = result[record.id] || result;

        if (fileData.attachment) {
          const link = document.createElement("a");
          link.href = `data:${record.filetype};base64,${fileData.attachment}`;
          link.download = record.filename || record.notes_title;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          message.success("Download started");
        } else {
          message.error("No attachment data found");
        }
      } else {
        message.error("Failed to retrieve attachment");
      }
    } catch (error) {
      message.error("Download failed");
      console.error("Download error:", error);
    }
  };

  // Handle unlink (remove relationship, not delete document)
  const handleUnlink = async (docId: string) => {
    try {
      const response = await axios.post("/api/direct/unset-relation", {
        relate_this_id: entityId,
        with_these_ids: [docId],
      });

      if (response.data.success) {
        message.success("Document unlinked");
        fetchDocuments(); // Refresh list
      } else {
        message.error(response.data.error || "Failed to unlink document");
      }
    } catch (error) {
      message.error("Failed to unlink document");
      console.error("Unlink error:", error);
    }
  };

  const uploadProps: UploadProps = {
    beforeUpload: handleUpload,
    showUploadList: false,
    multiple: false,
  };

  const columns = [
    {
      title: "Document",
      dataIndex: "notes_title",
      key: "notes_title",
      render: (value: string, record: DocumentRecord) => (
        <Space>
          {getFileIcon(record.filetype)}
          <span>{value}</span>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "filetype",
      key: "filetype",
      width: 100,
      render: (value: string) => (
        <Tag color="blue">{value?.split("/")[1] || value || "-"}</Tag>
      ),
    },
    {
      title: "Size",
      dataIndex: "filesize",
      key: "filesize",
      width: 80,
      render: (value: string) => formatFileSize(Number(value)),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: DocumentRecord) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/documents/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Download">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Unlink this document?"
            description="The document won't be deleted, just unlinked from this record."
            onConfirm={() => handleUnlink(record.id)}
            okText="Unlink"
            cancelText="Cancel"
          >
            <Tooltip title="Unlink">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Compact view for sidebar
  if (compact) {
    return (
      <Card
        title={title}
        size="small"
        extra={
          <Tooltip title="Add Document">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setUploadModalVisible(true)}
            />
          </Tooltip>
        }
        styles={{ body: { padding: 0 } }}
        style={{ marginBottom: 16 }}
      >
        {loading ? (
          <div style={{ padding: "12px", textAlign: "center" }}>Loading...</div>
        ) : documents.length > 0 ? (
          <div style={{ maxHeight: 150, overflowY: "auto" }}>
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 12px",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                  }}
                >
                  {getFileIcon(doc.filetype)} {doc.notes_title || doc.filename}
                </span>
                <Tooltip title="View">
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/documents/${doc.id}`)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              padding: "12px",
              textAlign: "center",
              color: "#999",
              fontSize: 12,
            }}
          >
            No documents
          </div>
        )}

        {/* Upload Modal */}
        <Modal
          title="Upload Document"
          open={uploadModalVisible}
          onCancel={() => setUploadModalVisible(false)}
          footer={null}
        >
          <Dragger {...uploadProps} disabled={uploading}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {uploading
                ? "Uploading..."
                : "Click or drag file to this area to upload"}
            </p>
            <p className="ant-upload-hint">
              The file will be uploaded and automatically linked to this record.
            </p>
          </Dragger>
        </Modal>
      </Card>
    );
  }

  return (
    <Card
      title={title}
      size="small"
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setUploadModalVisible(true)}
        >
          Add Document
        </Button>
      }
      style={{ marginTop: 16 }}
    >
      {documents.length > 0 ? (
        <Table
          dataSource={documents}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          loading={loading}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No documents attached"
        >
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => setUploadModalVisible(true)}
          >
            Upload Document
          </Button>
        </Empty>
      )}

      {/* Upload Modal */}
      <Modal
        title="Upload Document"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
      >
        <Dragger {...uploadProps} disabled={uploading}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {uploading
              ? "Uploading..."
              : "Click or drag file to this area to upload"}
          </p>
          <p className="ant-upload-hint">
            The file will be uploaded and automatically linked to this record.
          </p>
        </Dragger>
      </Modal>
    </Card>
  );
};

export default RelatedDocuments;
